import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { Select } from '../../ui';
import { AppLocation } from '../AppLocation';
import { useResolve } from '../Container';
import { AccountModel } from '../account/AccountModel';
import { labelSorter } from '../dashboard/Models';

type LocationItem = {
    key: string,
    label: string,
    fullLabel: string,
    parent?: LocationItem,
};

const itemToResult = (item: LocationItem) => {
    const { key, parent } = item;
    return {
        workspaceId: !!parent ? parent.key : key,
        folderId: !!parent ? key : null,
    };
};
const locationItem = (key: string, label: string, parent?: LocationItem) => ({
    key,
    label: `${!!parent ? '└── ' : ''}${label}`,
    fullLabel: [parent?.label, label.replace('└──', '').trim()].filter(Boolean).join(' / '),
    parent,
});

const useLocationItems = (mode: 'user' | 'admin') => {
    const api = useResolve(ApiClient);
    const { workspaces } = useObserver(useResolve(AccountModel));

    return useQuery({
        queryKey: ['team-location-items', mode],
        async queryFn() {
            if (mode === 'admin') {
                const ws = await api.exec(v2.admin.workspaces.list());
                return ws.flatMap(w => {
                    const p = locationItem(w.id, w.label, null);
                    return [p, ...Array.from(w?.folders ?? []).sort(labelSorter).map(f => locationItem(f.id, f.label, p))];
                });
            }
            else {
                const ws = [
                    ...workspaces.filter(x => x.type === 'team').sort(labelSorter),
                    ...workspaces.filter(x => x.type === 'user').sort(labelSorter),
                    // ...workspaces.filter(x => x.type === 'default').sort(labelSorter),
                ];
                return ws.flatMap(w => {
                    const p = locationItem(w.id, w.label, null);
                    return [p, ...Array.from(w?.folders ?? []).sort(labelSorter).map(f => locationItem(f.id, f.label, p))];
                });
            }
        },
    });

    // // Teams first
    // // Then private
    // // Then public
    // return useMemo(() => {
    //     const ws = [
    //         ...workspaces.filter(x => x.type === 'team').sort(labelSorter),
    //         ...workspaces.filter(x => x.type === 'user').sort(labelSorter),
    //         // ...workspaces.filter(x => x.type === 'default').sort(labelSorter),
    //     ];
    //     return ws.flatMap(w => {
    //         const p = locationItem(w.id, w.label, null);
    //         return [p, ...Array.from(w.folders).sort(labelSorter).map(f => locationItem(f.id, f.label, p))];
    //     });
    // }, [workspaces]);
};

const renderSelected = (item: LocationItem) => <>
    {!!item && <Select.InputText>{item.fullLabel}</Select.InputText>}
    {!item && <Select.InputPlaceholder>Select a team/folder...</Select.InputPlaceholder>}
</>;

const useGetDefaultLocation = () => {
    const location = useResolve(AppLocation);
    return useCallback((items: LocationItem[], initial?: string) => {
        const workspaceId = location.get('workspace');
        const folderId = location.get('boards');
        return (
            items.find(x => x.key === initial) ||
            items.find(x => x.key === folderId) ||
            items.find(x => x.key === workspaceId) ||
            items[0]
        );
    }, []);
};


export type TeamLocationSelectViewProps = {
    initial?: string,
    mode: 'user' | 'admin',
    onChange: (item: { workspaceId: string, folderId?: string }) => void,
};

export const TeamLocationSelectView = React.memo<TeamLocationSelectViewProps>(({ initial, mode, onChange }) => {

    const getDefaultLocation = useGetDefaultLocation();
    const { data } = useLocationItems(mode);
    const [selected, setSelected] = useState<LocationItem>(
        getDefaultLocation(data ?? [], initial)
    );

    useEffect(() => {
        setSelected(getDefaultLocation(data ?? [], initial));
    }, [data]);

    const handleChange = useCallback((item: LocationItem) => {
        setSelected(item);
        onChange(itemToResult(item));
    }, [onChange]);

    useEffect(() => {
        if (selected) {
            onChange(itemToResult(selected));
        }
    }, [selected]);

    return <Select
        items={data ?? []}
        selected={selected}
        onChange={handleChange}
        renderSelected={renderSelected}
    />;
});
